import { of, isNonEmpty, headNonEmpty, tailNonEmpty, prepend } from '../../Chunk/dist/effect-Chunk.esm.js';
import { equals } from '../../Equal/dist/effect-Equal.esm.js';
import { dual } from '../../Function/dist/effect-Function.esm.js';
import { reduce, get, remove, set } from '../../HashMap/dist/effect-HashMap.esm.js';
import { Structural } from '../Data.esm.js';

/** @internal */
const HashMapPatchTypeId = /*#__PURE__*/Symbol.for("effect/DifferHashMapPatch");
function variance(a) {
  return a;
}

/** @internal */
const PatchProto = {
  ...Structural.prototype,
  [HashMapPatchTypeId]: {
    _Value: variance,
    _Key: variance,
    _Patch: variance
  }
};
const EmptyProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Empty"
});
const _empty = /*#__PURE__*/Object.create(EmptyProto);

/** @internal */
const empty = () => _empty;
const AndThenProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "AndThen"
});
const makeAndThen = (first, second) => {
  const o = Object.create(AndThenProto);
  o.first = first;
  o.second = second;
  return o;
};
const AddProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Add"
});
const makeAdd = (key, value) => {
  const o = Object.create(AddProto);
  o.key = key;
  o.value = value;
  return o;
};
const RemoveProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Remove"
});
const makeRemove = key => {
  const o = Object.create(RemoveProto);
  o.key = key;
  return o;
};
const UpdateProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Update"
});
const makeUpdate = (key, patch) => {
  const o = Object.create(UpdateProto);
  o.key = key;
  o.patch = patch;
  return o;
};
/** @internal */
const diff = options => {
  const [removed, patch] = reduce([options.oldValue, empty()], ([map, patch], newValue, key) => {
    const option = get(key)(map);
    switch (option._tag) {
      case "Some":
        {
          const valuePatch = options.differ.diff(option.value, newValue);
          if (equals(valuePatch, options.differ.empty)) {
            return [remove(key)(map), patch];
          }
          return [remove(key)(map), combine(makeUpdate(key, valuePatch))(patch)];
        }
      case "None":
        {
          return [map, combine(makeAdd(key, newValue))(patch)];
        }
    }
  })(options.newValue);
  return reduce(patch, (patch, _, key) => combine(makeRemove(key))(patch))(removed);
};

/** @internal */
const combine = /*#__PURE__*/dual(2, (self, that) => makeAndThen(self, that));

/** @internal */
const patch = /*#__PURE__*/dual(3, (self, oldValue, differ) => {
  let map = oldValue;
  let patches = of(self);
  while (isNonEmpty(patches)) {
    const head = headNonEmpty(patches);
    const tail = tailNonEmpty(patches);
    switch (head._tag) {
      case "Empty":
        {
          patches = tail;
          break;
        }
      case "AndThen":
        {
          patches = prepend(head.first)(prepend(head.second)(tail));
          break;
        }
      case "Add":
        {
          map = set(head.key, head.value)(map);
          patches = tail;
          break;
        }
      case "Remove":
        {
          map = remove(head.key)(map);
          patches = tail;
          break;
        }
      case "Update":
        {
          const option = get(head.key)(map);
          if (option._tag === "Some") {
            map = set(head.key, differ.patch(head.patch, option.value))(map);
          }
          patches = tail;
          break;
        }
    }
  }
  return map;
});

export { HashMapPatchTypeId, combine, diff, empty, patch };
